'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import Link from 'next/link';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import CtaBtn from 'shared/components/CtaBtn';
import GeneralContactForm from 'shared/components/GeneralContactForm';
import useScrollHeight from 'shared/hooks/useScrollHeight';
import useWindowWidth from 'shared/hooks/useWindowWidth';
import { Dictionaries, Locale } from 'shared/i18n/i18n-config';
import { getI18nUtils } from 'shared/i18n/i18n-utils';
import { buttons, services, strengths, timeline } from './config';

type Props = {
  dictionary: Partial<Dictionaries>;
  locale: Locale;
};

export default function SynergieCare({ locale, dictionary }: Props) {
  const { formatMessage } = getI18nUtils(dictionary);
  const windowWidth = useWindowWidth();
  const scrollHeight = useScrollHeight();
  const onMobile = windowWidth && windowWidth < 992;
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState([false, false, false, false]);
  const academyPath = `/${locale}/employers/academy/`;
  const synergieGoPath = `/${locale}/employers/synergiego/`;

  useEffect(() => {
    if (visible) return;
    if (scrollHeight > 500) setVisible(true);
  }, [scrollHeight, visible]);

  const triggerSequence = useCallback(() => {
    isVisible.forEach((_, i) => {
      setTimeout(() => {
        setIsVisible((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, i * 350);
    });
  }, [isVisible]);

  useEffect(() => {
    const node = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          triggerSequence();
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.6 }
    );

    if (node) observer.observe(node);
    return () => {
      if (node) observer.unobserve(node);
    };
  }, [triggerSequence]);

  return (
    <>
      <Container>
        <Row className="position-relative">
          <Col lg={{ offset: 3, size: 9 }} className="d-lg-flex justify-content-lg-end">
            <div id="main-cta" className={`floating-button ${visible ? 'visible' : ''} ${scrollHeight > 500 ? 'fixed' : ''}`}>
              <Link href="#contact" className="btn btn-primary btn-care">
                <span className="me-2">{formatMessage({ id: 'employers-care-ctabtn' })}</span>
                <svg height="12" viewBox="0 0 8 12" width="8" xmlns="http://www.w3.org/2000/svg">
                  <g fillRule="evenodd">
                    <path
                      fill="white"
                      d="m1.1117.02285-.8838.883775 4.7267 4.726525-4.7267 4.7265.8838.883775 5.610525-5.610275z"
                      fillRule="nonzero"
                      transform="translate(.5 .25)"
                    />
                  </g>
                </svg>
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
      <div className="synergie-care">
        <Container className="bg-white d-flex justify-content-center py-5 px-0">
          <Row className="w-100 d-flex justify-content-center align-items-center">
            <Col className="d-none d-lg-block me-1">
              <hr className="border-2 opacity-50 text-black" />
            </Col>
            <Col xs={12} lg="auto" className="d-flex justify-content-center">
              <Row className="d-flex align-items-center gx-0 w-100">
                {buttons.map((button, index) => (
                  <Col xs={6} md={4} lg="auto" className="p-1" key={'button-' + index}>
                    <Link href={button.link}>
                      <Button outline className="w-100">
                        {formatMessage({ id: button.name })}
                      </Button>
                    </Link>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col className="d-none d-lg-block ms-1">
              <hr className="border-2 opacity-50 text-black" />
            </Col>
          </Row>
        </Container>

        <section id="care" className="care-intro-section">
          <Container>
            <Row className="align-items-center gy-4">
              <Col lg={4} className="mb-4 mb-lg-0">
                <Image src="/images/care/synergie_care_logo.png" alt="Synergie Care" width={225} height={225} className="" />
              </Col>
              <Col lg={8}>
                <h2 className="care-title care-title-medium care-intro-title mb-3">
                  {formatMessage({ id: 'employers-care-about-title' })}
                </h2>
                <p className="mb-3">{formatMessage({ id: 'employers-care-about-description-1' })}</p>
                <p>{formatMessage({ id: 'employers-care-about-description-2' })}</p>
              </Col>
            </Row>
          </Container>
        </section>

        <section id="services" className="pt-5 pb-5 bg-white">
          <Container>
            <Row className="align-items-center g-4">
              <Col lg={7}>
                <h2 className="care-title care-title-medium text-uppercase mb-3">
                  {formatMessage({ id: 'employers-care-services-title' })}
                </h2>
                <p>{formatMessage({ id: 'employers-care-services-intro' })}</p>
                <ul className="care-list">
                  {services.map((item) => (
                    <li key={item}>
                      {formatMessage(
                        { id: item },
                        {
                          academyLink: <Link href={academyPath}>Synergie Academy</Link>,
                          synergieGoLink: <Link href={synergieGoPath}>SynergieGo</Link>,
                          sandYouLink: (
                            <a href="https://www.sandyou.be/nl/employer/recruitment/" target="_blank" rel="noopener noreferrer">
                              S&you
                            </a>
                          ),
                        }
                      )}
                    </li>
                  ))}
                </ul>
              </Col>
              <Col lg={5}>
                <Image
                  src="/images/care/voor_elke_job_de_juiste_kandidaat_zorg.jpg"
                  alt=""
                  className="mw-100 h-auto"
                  width={613}
                  height={429}
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col xs={12}>
                <div className="care-employers-cta">
                  <div className="care-employers-cta-content">
                    <h3 className="h5 mb-2">{formatMessage({ id: 'employers-care-employers-title' })}</h3>
                    <p className="mb-0">{formatMessage({ id: 'employers-care-employers-intro' })}</p>
                  </div>
                  <Link href="#contact">
                    <CtaBtn className="cta red" type="button">
                      {formatMessage({ id: 'employers-care-employers-ctabtn' })}
                    </CtaBtn>
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section id="why" className="pt-5 pb-5 bg-light">
          <Container>
            <Row className="align-items-center g-4">
              <Col lg={5}>
                <Image
                  src="/images/care/troeven_voor_vlotte_samenwerkingen.jpg"
                  alt=""
                  className="mw-100 h-auto"
                  width={613}
                  height={429}
                />
              </Col>
              <Col lg={7}>
                <h2 className="care-title care-title-semibold text-uppercase mb-3">
                  {formatMessage({ id: 'employers-care-strengths-title' })}
                </h2>
                <p className={'mb-3'}>{formatMessage({ id: 'employers-care-strengths-intro-1' })}</p>
                <p>{formatMessage({ id: 'employers-care-strengths-intro-2' })}</p>
                <ul className="care-list mb-0">
                  {strengths.map((item) => (
                    <li key={item}>{formatMessage({ id: item })}</li>
                  ))}
                </ul>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="pt-5 pb-5 bg-white">
          <div className="care-candidates-banner">
            <Container>
              <div className="care-candidates-content">
                <h2 className="care-title care-title-medium text-uppercase mb-3">
                  {formatMessage({ id: 'employers-care-candidates-title' })}
                </h2>
                <p>{formatMessage({ id: 'employers-care-candidates-description' })}</p>
                <Link href={`/${locale}/jobs/?jobDomains=jobdomain-healthcare`}>
                  <CtaBtn className="cta red" type="button">
                    {formatMessage({ id: 'employers-care-candidates-ctabtn' })}
                  </CtaBtn>
                </Link>
              </div>
            </Container>
          </div>
        </section>

        <section id="approach" className="pt-4 pb-5 bg-white care-approach-section">
          <Container>
            <Row>
              <Col lg={5} className="mb-4 mb-lg-0">
                <h2 className="care-title care-title-semibold text-uppercase mb-4">
                  {formatMessage({ id: 'employers-care-approach-title' })}
                </h2>
                <p>{formatMessage({ id: 'employers-care-approach-intro' })}</p>
                <Image
                  className="object-fit-cover w-100 h-auto care-approach-image"
                  src="/images/care/onze_persoonlijke_aanpak_care.jpg"
                  alt={formatMessage({ id: 'employers-care-approach-title' })}
                  width={1024}
                  height={819}
                />
              </Col>
              <Col lg={7}>
                <div ref={ref} className="h-100 px-5">
                  {timeline.map((item, index) => (
                    <div className={`care-timeline-item ${isVisible[index] ? 'visible' : ''}`} key={item.title}>
                      <p className="care-timeline-title">{formatMessage({ id: item.title })}</p>
                      <p className="care-timeline-description">{formatMessage({ id: item.description })}</p>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section id="contact" className="pt-5 pb-5 bg-light">
          <Container className="mt-md-4 mb-md-4">
            <Row>
              <Col xs={12} lg={6}>
                <div style={{ maxWidth: '460px' }}>
                  <h2 className="care-title care-title-medium mb-4">{formatMessage({ id: 'employers-care-form-title' })}</h2>
                  <p>{formatMessage({ id: 'employers-care-form-description-1' })}</p>
                  <p>
                    {formatMessage(
                      { id: 'employers-care-form-description-2' },
                      {
                        mailtoLink: (
                          <Link className="d-block" href="mailto:care@synergiejobs.be">
                            care@synergiejobs.be
                          </Link>
                        ),
                      }
                    )}
                  </p>
                  <p>{formatMessage({ id: 'employers-care-form-description-3' })}</p>
                </div>
              </Col>
              <Col xs={12} lg={6}>
                <Card id="contactForm" className="rounded-0 mb-0 care-form-card">
                  <CardBody>
                    <GeneralContactForm
                      compact={!onMobile}
                      locale={locale}
                      dictionary={dictionary}
                      submitHandler={setSubmitting}
                      submitting={submitting}
                      confirmationMessage={
                        <div>
                          <h4 className="mb-3">{formatMessage({ id: 'employers-care-submit-title' })}</h4>
                          <p className="mb-0">{formatMessage({ id: 'employers-care-submit-message-1' })}</p>
                          <p>{formatMessage({ id: 'employers-care-submit-message-2' })}</p>
                          <p className="mb-0">{formatMessage({ id: 'employers-care-submit-message-3' })}</p>
                          <p>{formatMessage({ id: 'employers-care-submit-message-4' })}</p>
                        </div>
                      }
                      hasMessage={false}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>
      </div>
    </>
  );
}
